import React, { useEffect, useRef, useState, forwardRef, memo } from 'react'
import styled, { css } from 'styled-components'
import { Close, KeyboardArrowDown, KeyboardArrowUp } from '@material-ui/icons'
import { Spacings, Text, Devices } from '@styles'
import { debounce } from '@utils'
import { FormElementContainer, Label, ErrorMessage, baseInputStyles } from '../styled'

const SelectContainer = styled(FormElementContainer)`
	position: relative;
`

const SelectInputWrapper = styled.div`
	position: relative;
	width: 100%;
`

const StyledInput = styled.input`
	${baseInputStyles}
	padding-right: ${Spacings.SPACING_9B};
	cursor: pointer;
	text-overflow: ellipsis;

	@media ${Devices.tablet} {
		padding-right: ${Spacings.SPACING_9B};
	}

	${({ $small }) =>
		$small &&
		css`
			padding-top: ${Spacings.SPACING_2};
			padding-bottom: ${Spacings.SPACING_2};
			font-size: ${Text.SMALL};
			height: ${Spacings.SPACING_8B};
		`}
`

const IconWrapper = styled.div`
	position: absolute;
	right: ${Spacings.SPACING_2B};
	top: 50%;
	transform: translateY(-50%);
	display: flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;
	color: ${({ theme }) => theme.colors.text.secondary};
	padding: ${Spacings.SPACING_1};
	border-radius: 50%;
	transition: all 0.2s ease-in-out;

	svg {
		font-size: ${Text.LARGE};
	}

	&:hover {
		background: ${({ theme }) => theme.colors.brand.subtle};
		color: ${({ theme }) => theme.colors.brand.primary};
	}
`

const OptionsList = styled.div`
	position: absolute;
	${({ $openAbove }) => ($openAbove ? `bottom: calc(100% + ${Spacings.SPACING_1B});` : `top: calc(100% + ${Spacings.SPACING_1B});`)}
	left: 0;
	width: 100%;
	background: ${({ theme }) => theme.colors.surface.card};
	border: 1px solid ${({ theme }) => theme.colors.border.default};
	border-radius: ${({ theme }) => theme.radius.md};
	box-shadow: ${({ theme }) => theme.shadows.md};
	z-index: 600;
	max-height: calc(15 * ${Spacings.SPACING_4B});
	overflow-y: auto;
	box-sizing: border-box;

	scrollbar-width: thin;
	&::-webkit-scrollbar {
		width: ${Spacings.SPACING_6};
	}
	&::-webkit-scrollbar-thumb {
		background-color: ${({ theme }) => theme.colors.border.default};
		border-radius: calc(${Spacings.SPACING_6} / 2);
	}
`

const Option = styled.div`
	${({ $grouped }) => $grouped && `margin-left: ${Spacings.SPACING_2B};`}
	padding: ${Spacings.SPACING_2B} ${Spacings.SPACING_3B};
	font-size: ${Text.SMALL};
	font-weight: ${Text.BOLD_400};
	color: ${({ theme }) => theme.colors.text.primary};
	cursor: pointer;
	display: flex;
	align-items: center;
	transition:
		background 0.15s ease-in-out,
		color 0.15s ease-in-out;
	font-family: inherit;
	box-sizing: border-box;

	@media ${Devices.tablet} {
		padding: ${Spacings.SPACING_2B} ${Spacings.SPACING_4B};
		font-size: ${Text.MEDIUM};
	}

	&:hover {
		background: ${({ theme }) => theme.colors.brand.subtle};
		color: ${({ theme }) => theme.colors.brand.primary};
	}

	${({ selected, theme }) =>
		selected &&
		css`
			background: ${theme.colors.brand.subtle};
			color: ${theme.colors.brand.primary};
			font-weight: ${Text.BOLD_700};
		`}

	${({ highlighted, theme }) =>
		highlighted &&
		css`
			background: ${theme.colors.brand.subtle};
		`}

	${({ $disabled, theme }) =>
		$disabled &&
		css`
			color: ${theme.colors.text.secondary};
			cursor: not-allowed;

			&:hover {
				background: transparent;
				color: ${theme.colors.text.secondary};
			}
		`}

	span {
		display: block;
		width: 100%;
		min-width: 0;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
	}
`

const NoOption = styled(Option)`
	pointer-events: none;
	color: ${({ theme }) => theme.colors.text.secondary};
	justify-content: center;
`

// heading of a group of options, set by the group key
const GroupLabel = styled.div`
	padding: ${Spacings.SPACING_2B} ${Spacings.SPACING_3B};
	font-size: ${Text.SMALL};
	font-weight: ${Text.BOLD_600};
	color: ${({ theme }) => theme.colors.text.primary};
	background: ${({ theme }) => theme.colors.surface.overlay};
	text-transform: uppercase;
	pointer-events: none;
	box-sizing: border-box;

	@media ${Devices.tablet} {
		padding: ${Spacings.SPACING_2B} ${Spacings.SPACING_4B};
	}
`

const CreateItemButton = styled.button`
	width: 100%;
	border: none;
	background: ${({ theme }) => theme.colors.brand.primary};
	color: ${({ theme }) => theme.colors.text.inverse};
	font-weight: ${Text.BOLD_600};
	cursor: pointer;
	padding: ${Spacings.SPACING_2B};
	border-radius: ${({ theme }) => theme.radius.sm};
	margin-top: ${Spacings.SPACING_1B};
	transition: background 0.2s ease;
	font-family: inherit;

	&:hover {
		background: ${({ theme }) => theme.colors.brand.hover};
	}
`
const EMPTY_OPTIONS = []

const isSameOptionList = (a, b) => {
	if (a === b) return true
	if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false
	return a.every((item, index) => item === b[index])
}

const Select = forwardRef(
	(
		{
			id,
			label,
			data = EMPTY_OPTIONS,
			value,
			displayKey,
			primaryKey,
			onChange,
			placeholder,
			disabled,
			readOnly,
			small,
			noDropIcon,
			uppercase,
			error,
			createNewOption,
			cancelClick,
			className,
			placeholderFontSize,
			onInputChange,
			escapeOverflow,
			groupKey,
			openAbove,
			...props
		},
		ref
	) => {
		const elemRef = useRef(null)
		const listRef = useRef(null)
		const cancelRef = useRef(null)
		const filterFunctionRef = useRef(null)

		const [isOpen, setIsOpen] = useState(false)
		const [inputValue, setInputValue] = useState('')
		const [options, setOptions] = useState([])
		const [highlightedIndex, setHighlightedIndex] = useState(-1)
		const [isFocused, setIsFocused] = useState(false)
		const [floatingRect, setFloatingRect] = useState(null)

		const hasError = !!error
		const showCloseIcon = (isFocused && inputValue) || cancelClick

		useEffect(() => {
			filterFunctionRef.current = debounce(filterOptions, 200)
		}, [])

		useEffect(() => {
			if (value !== undefined && value !== null) {
				setInputValue(value[displayKey] ?? '')
			} else {
				setInputValue('')
			}
		}, [value, displayKey])

		useEffect(() => {
			const handleClickOutside = (e) => {
				const target = e.target
				if (
					target &&
					elemRef.current &&
					!elemRef.current.contains(target) &&
					(!listRef.current || !listRef.current.contains(target)) &&
					(!cancelRef.current || !cancelRef.current.contains(target))
				) {
					setIsOpen(false)
					setIsFocused(false)
				}
			}

			if (isOpen) {
				window.addEventListener('mousedown', handleClickOutside)
			} else {
				window.removeEventListener('mousedown', handleClickOutside)
			}

			return () => {
				window.removeEventListener('mousedown', handleClickOutside)
			}
		}, [isOpen])

		// escapeOverflow: render the options list at a viewport-fixed position computed from the
		// input's own rect, instead of position:absolute relative to this component — so it's never
		// clipped by (or bled through) an ancestor's scroll/overflow container. Recomputed on open
		// and on any scroll (capture phase, since nested scroll containers don't bubble) or resize.
		useEffect(() => {
			if (!escapeOverflow || !isOpen) return

			const updateRect = () => {
				if (elemRef.current) {
					setFloatingRect(elemRef.current.getBoundingClientRect())
				}
			}

			updateRect()
			document.addEventListener('scroll', updateRect, true)
			window.addEventListener('resize', updateRect)

			return () => {
				document.removeEventListener('scroll', updateRect, true)
				window.removeEventListener('resize', updateRect)
			}
		}, [escapeOverflow, isOpen])

		useEffect(() => {
			if (isOpen) {
				setInputValue('')
				setOptions(data)
				const selectedIdx =
					value !== undefined && value !== null
						? (data ?? []).findIndex((item) => String(item[primaryKey]) === String(value[primaryKey]))
						: -1
				setHighlightedIndex(selectedIdx >= 0 ? selectedIdx : 0)
			} else {
				setHighlightedIndex(-1)
				if (value !== undefined && value !== null) {
					setInputValue(value[displayKey] ?? '')
				} else {
					setInputValue('')
				}
			}
		}, [isOpen])

		useEffect(() => {
			setOptions((prev) => (isSameOptionList(prev, data) ? prev : data))
		}, [data])

		useEffect(() => {
			if (isOpen) {
				const selectedIdx =
					value !== undefined && value !== null ? options.findIndex((item) => String(item[primaryKey]) === String(value[primaryKey])) : -1
				const idx = selectedIdx >= 0 ? selectedIdx : 0
				setHighlightedIndex(idx)
				setTimeout(() => scrollHighlightedIntoView(idx), 0)
			}
		}, [options])

		useEffect(() => {
			if (isOpen && filterFunctionRef.current) {
				filterFunctionRef.current(data, inputValue)
			}
		}, [inputValue])

		const filterOptions = (allData, searchString) => {
			if (searchString) {
				const query = searchString.toLowerCase().replaceAll(' ', '')
				const filtered = (allData ?? []).filter((option) => option[displayKey]?.toLowerCase().replaceAll(' ', '').includes(query))
				setOptions(filtered)
			} else {
				setOptions(allData)
			}
		}

		const changeHandler = (e) => {
			let val = e.target.value ?? ''
			if (uppercase) {
				val = val.toUpperCase()
			}
			setInputValue(val)
			onInputChange?.(val)
		}

		const focusHandler = () => {
			if (disabled || readOnly) return
			setIsOpen(true)
			setIsFocused(true)
		}

		const optionClickHandler = (item, e) => {
			e.stopPropagation()
			if (item.disabled) return
			if (value === undefined || value === null || String(value[primaryKey]) !== String(item[primaryKey])) {
				onChange(item)
			}
			setIsOpen(false)
			setIsFocused(false)
		}

		const cancelClickHandler = (e) => {
			e.stopPropagation()
			setInputValue('')
			if (elemRef.current) {
				elemRef.current.focus()
			}
			if (cancelClick) {
				cancelClick()
			}
			if (!showCloseIcon && isOpen) {
				setIsOpen(false)
				setIsFocused(false)
			}
		}

		const scrollHighlightedIntoView = (index) => {
			if (listRef.current) {
				const item = listRef.current.querySelector(`[data-highlight-index="${index}"]`)
				if (item) {
					item.scrollIntoView({ block: 'nearest' })
				}
			}
		}

		const onKeyDownHandler = (e) => {
			if (!isOpen) {
				if (e.keyCode === 40 || e.keyCode === 38 || e.keyCode === 13) {
					setIsOpen(true)
					e.preventDefault()
				}
				return
			}

			if (e.keyCode === 40) {
				// ArrowDown
				e.preventDefault()
				setHighlightedIndex((prev) => {
					const next = Math.min(prev + 1, options.length - 1)
					scrollHighlightedIntoView(next)
					return next
				})
				return
			}
			if (e.keyCode === 38) {
				// ArrowUp
				e.preventDefault()
				setHighlightedIndex((prev) => {
					const next = Math.max(prev - 1, 0)
					scrollHighlightedIntoView(next)
					return next
				})
				return
			}
			if (e.keyCode === 13) {
				// Enter
				if (options.length > 0 && highlightedIndex >= 0 && highlightedIndex < options.length) {
					e.preventDefault()
					const selectedOption = options[highlightedIndex]
					if (selectedOption.disabled) return
					if (value === undefined || value === null || String(value[primaryKey]) !== String(selectedOption[primaryKey])) {
						onChange(selectedOption)
					}
					setIsOpen(false)
					setIsFocused(false)
				}
			}
			if (e.keyCode === 27) {
				// Escape
				e.preventDefault()
				setIsOpen(false)
				setIsFocused(false)
			}
		}

		const onCreateItemClickHandler = (e) => {
			e.stopPropagation()
			setIsOpen(false)
			setIsFocused(false)
			const optionValue = {
				primaryKey: primaryKey ?? null,
				displayKey: displayKey ?? null,
				[primaryKey]: inputValue,
				[displayKey]: inputValue,
				is_new_item: true,
			}
			onChange(optionValue)
		}

		const renderIcon = () => {
			if (showCloseIcon) {
				return <Close />
			}
			return isOpen ? <KeyboardArrowUp /> : <KeyboardArrowDown />
		}

		const hasValue =
			value && typeof value === 'object' && Object.keys(value).length > 0 && value[primaryKey] !== undefined && value[primaryKey] !== null

		return (
			<SelectContainer className={className}>
				{label && <Label htmlFor={id}>{label}</Label>}
				<SelectInputWrapper>
					<StyledInput
						ref={(node) => {
							elemRef.current = node
							if (ref) {
								if (typeof ref === 'function') ref(node)
								else ref.current = node
							}
						}}
						id={id}
						disabled={disabled}
						readOnly={readOnly}
						$hasError={hasError}
						$small={small}
						$placeholderFontSize={placeholderFontSize}
						value={isOpen ? inputValue : hasValue ? (value[displayKey] ?? '') : ''}
						onChange={changeHandler}
						onFocus={focusHandler}
						onMouseDown={(e) => {
							// Toggle on clicking anywhere in the input (not just the dropdown icon).
							// First click focuses → focusHandler opens; subsequent clicks toggle open/close.
							if (disabled || readOnly) return
							if (document.activeElement === elemRef.current) {
								e.preventDefault()
								setIsOpen((prev) => !prev)
							}
						}}
						onKeyDown={onKeyDownHandler}
						placeholder={placeholder}
						autoComplete='off'
						{...props}
					/>
					{!noDropIcon && !readOnly && !disabled && (
						<IconWrapper onClick={cancelClickHandler} ref={cancelRef}>
							{renderIcon()}
						</IconWrapper>
					)}
				</SelectInputWrapper>
				{isOpen && (
					// Options aren't focusable, so a real mousedown on one would otherwise blur the
					// input before the click fires — preventing that keeps focus (and any ancestor's
					// `:focus-within` overflow escape hatch) intact through the click.
					<OptionsList
						ref={listRef}
						$openAbove={openAbove}
						onMouseDown={(e) => e.preventDefault()}
						style={
							escapeOverflow && floatingRect
								? { position: 'fixed', top: floatingRect.bottom + 4, left: floatingRect.left, width: floatingRect.width }
								: undefined
						}
					>
						{options.length > 0 ? (
							options.map((item, index) => {
								const selected = value !== undefined && value !== null && String(item[primaryKey]) === String(value[primaryKey])
								const highlighted = index === highlightedIndex
								// with a group key, a heading is shown where the group changes, so the data has to be ordered by group
								const showGroup = !!groupKey && !!item[groupKey] && item[groupKey] !== options[index - 1]?.[groupKey]
								return (
									<React.Fragment key={`${id}-option-${item[primaryKey] || index}`}>
										{showGroup && <GroupLabel>{item[groupKey]}</GroupLabel>}
										<Option
											data-highlight-index={index}
											selected={selected}
											highlighted={highlighted}
											$disabled={item.disabled}
											$grouped={!!groupKey && !!item[groupKey]}
											onClick={(e) => optionClickHandler(item, e)}
										>
											<span>{item[displayKey]}</span>
										</Option>
									</React.Fragment>
								)
							})
						) : (
							<>
								<NoOption>No match found</NoOption>
								{!!inputValue && createNewOption && (
									<Option>
										<CreateItemButton onClick={onCreateItemClickHandler}>Click here to select entered product</CreateItemButton>
									</Option>
								)}
							</>
						)}
					</OptionsList>
				)}
				{error && <ErrorMessage>{error}</ErrorMessage>}
			</SelectContainer>
		)
	}
)

Select.displayName = 'Select'

export default memo(Select)
